import { PropsWithChildren, useContext } from "react";
import { createPortal } from "react-dom";
import { Editor } from "@tiptap/react";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  TextQuoteIcon,
  Link2Icon,
} from "lucide-react";
import {
  Root,
  Button as ToolbarButton,
  Separator,
  type ToolbarButtonProps,
} from "@radix-ui/react-toolbar";
import cx from "classnames";

import { useCustomLinkStore } from "../CustomLink";
import { BubbleMenu } from "../BubbleMenu";
import { LinkPanel } from "../LinkPanel/LinkPanel";
import { linkPanelContext } from "../LinkPanel/context";

export const sharedToolbarCss =
  "overflow-hidden rounded p-1 shadow-xl bg-black";

function CustomToolbarButton({
  children,
  isActive,
  ...rest
}: PropsWithChildren & ToolbarButtonProps & { isActive: boolean }) {
  const className = cx("flex items-center justify-center w-6 h-6 rounded-xs", {
    "bg-white text-black hover:bg-gray-100": isActive,
    "bg-black text-white hover:bg-gray-700": !isActive,
  });

  return (
    <ToolbarButton {...rest} className={className}>
      {children}
    </ToolbarButton>
  );
}

export const Toolbar = ({ editor }: { editor: Editor }) => {
  const { refs, setIsPoppableVisible, isPoppableVisible } =
    useContext(linkPanelContext);
  const { isLinkPanelVisible } = useCustomLinkStore();

  return (
    <>
      <BubbleMenu
        editor={editor}
        open={
          isLinkPanelVisible ||
          !(
            !editor.isFocused ||
            editor.isActive("footnote") ||
            editor.isActive("codeBlock") ||
            editor.isActive("image") ||
            editor.isActive("figure") ||
            editor.isActive("horizontalRule") ||
            editor.isActive("heading", { level: 1 }) ||
            editor.isActive("heading", { level: 2 }) ||
            editor.isActive("heading", { level: 3 }) ||
            editor.isActive("heading", { level: 4 }) ||
            editor.isActive("embed-preview")
          )
        }
      >
        <div ref={refs?.setReference}>
          <Root className={cx(sharedToolbarCss, "flex gap-1")}>
            <CustomToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive("bold")}
            >
              <BoldIcon width={12} height={12} />
            </CustomToolbarButton>
            <CustomToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive("italic")}
            >
              <ItalicIcon width={12} height={12} />
            </CustomToolbarButton>
            <CustomToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive("underline")}
            >
              <UnderlineIcon width={12} height={12} />
            </CustomToolbarButton>
            <Separator className="mt-1 mb-1 w-0.5 bg-gray-800" />
            <CustomToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive("blockquote")}
            >
              <TextQuoteIcon width={12} height={12} />
            </CustomToolbarButton>
            <Separator className="mt-1 mb-1 w-0.5 bg-gray-800" />
            <CustomToolbarButton
              onClick={
                editor.isActive("link")
                  ? () => editor.chain().focus().unsetLink().run()
                  : () => setIsPoppableVisible(true)
              }
              isActive={editor.isActive("link")}
            >
              <Link2Icon width={12} height={12} />
            </CustomToolbarButton>
          </Root>
        </div>
      </BubbleMenu>
      {isPoppableVisible &&
        createPortal(
          <LinkPanel
            ref={refs?.setFloating}
            editor={editor}
            onLinkSet={() => setIsPoppableVisible(false)}
          />,
          document.getElementById("editor-area") as HTMLDivElement
        )}
    </>
  );
};
